import { useMemo } from 'react';
import { useDynamicConfig } from '@statsig/react-bindings';

import { DynamicConfigs } from 'modules/common/constants/featuregates';

const PLAID_INSTITUTION_ID = /^ins_\d+$/;
const QUILTT_INSTITUTION_ID = /^inst_[A-Za-z0-9]+$/;

function isIdList(value: unknown, pattern: RegExp): value is string[] {
    return Array.isArray(value) && value.length > 0 && value.every((id) => typeof id === 'string' && pattern.test(id));
}

/**
 * The Plaid institution ids listed in `quiltt_unreliable_institutions`, read with the backend's rules
 * (`QuilttUnreliableInstitutions` in `hyke/featureflags/configs.py`). Any invalid entry makes the backend
 * read the whole config as listing no banks, so it does here too: otherwise the two would disagree about
 * which banks are withdrawn.
 */
export function listedPlaidInstitutionIds(institutions: unknown): string[] {
    if (!Array.isArray(institutions)) {
        return [];
    }
    const isValid = institutions.every(
        (bank) =>
            typeof bank === 'object' &&
            bank !== null &&
            typeof bank.name === 'string' &&
            isIdList(bank.plaid_ids, PLAID_INSTITUTION_ID) &&
            isIdList(bank.quiltt_ids, QUILTT_INSTITUTION_ID)
    );
    return isValid ? institutions.flatMap((bank) => bank.plaid_ids) : [];
}

/** The Plaid institution ids of the banks we no longer offer Auto Statements for. */
export function useUnreliablePlaidInstitutionIds(): readonly string[] {
    const config = useDynamicConfig(DynamicConfigs.QuilttUnreliableInstitutions);
    return useMemo(() => listedPlaidInstitutionIds(config.get('institutions', [])), [config]);
}

/**
 * Whether Auto Statements is withdrawn at a bank. Either signal is enough on its own: the availability
 * flag (`offer_auto_statements`) is `false`, or the bank's Plaid id is listed. The two can disagree:
 * availability has no row for a bank whose accounts are all personal, and the backend also matches the
 * flag on the bank's Quiltt id. A missing flag and a missing id both mean "offered".
 */
export function isAutoStatementsWithdrawn(
    offerAutoStatements: boolean | undefined,
    plaidInstitutionId: string | null | undefined,
    listedPlaidIds: readonly string[]
): boolean {
    return offerAutoStatements === false || (!!plaidInstitutionId && listedPlaidIds.includes(plaidInstitutionId));
}

/** Whether `plaidInstitutionId` is a bank we no longer offer Auto Statements for. */
export function useIsUnreliableInstitution(plaidInstitutionId: string | null | undefined): boolean {
    const listedPlaidIds = useUnreliablePlaidInstitutionIds();
    return !!plaidInstitutionId && listedPlaidIds.includes(plaidInstitutionId);
}
